// AssignOfficerDialog.js
//
// Big dialog opened from GroupsView.js's "Assign Officer" button. Two
// distinct jobs live here, kept as separate tabs so they don't get
// confused with each other:
//
//  1. "Meeting Officers" - who is assigned to the group's current/next
//     meeting. Exactly what DetailedInfo.js's reschedule form already does
//     (reassign_officers on the reschedule endpoint) - reused here so
//     there's one source of truth for "who's assigned to this meeting".
//
//  2. "Extended Data Access" - a new, separate concept. Normally an
//     officer assigned to a meeting can only sync/update that group's
//     member data for meeting_date -1/0/+1 (three days). When that's not
//     enough (meeting was too long ago, officer needs to catch up), a
//     supervisor grants explicit, time-boxed access here instead of
//     touching the meeting. See OfficerDataAccessGrant in diary/models.py.
//
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  IconButton,
  Checkbox,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Alert,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import LockClockIcon from '@mui/icons-material/LockClock';
import BlockIcon from '@mui/icons-material/Block';
import EventIcon from '@mui/icons-material/Event';
import { diaryAPI, handleAPIError } from './api';

// Quick expiry presets for how long a grant stays usable. 24 hours is the
// default - short-lived on purpose, so access doesn't linger unnoticed.
const EXPIRY_PRESETS = [
  { key: '24h', label: '24 hours', hours: 24 },
  { key: '3d', label: '3 days', hours: 72 },
  { key: '7d', label: '7 days', hours: 168 },
];
const DEFAULT_EXPIRY_PRESET = '24h';

const officerDisplayName = (officer) =>
  `${officer.first_name || ''} ${officer.last_name || ''}`.trim() || officer.username;

const officerInitials = (officer) => {
  const name = officerDisplayName(officer);
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
};

const grantStatus = (grant) => {
  if (grant.revoked_at) return { label: 'Revoked', color: 'default' };
  if (grant.is_expired) return { label: 'Expired', color: 'default' };
  return { label: 'Active', color: 'success' };
};

const formatMeetingDate = (dateString) => {
  if (!dateString) return 'Not scheduled';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const AssignOfficerDialog = ({ open, onClose, group, onUpdated }) => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  const [availableOfficers, setAvailableOfficers] = useState([]);
  const [loadingOfficers, setLoadingOfficers] = useState(false);

  const [selectedOfficerIds, setSelectedOfficerIds] = useState([]);
  const [savingMeetingOfficers, setSavingMeetingOfficers] = useState(false);

  const [grants, setGrants] = useState([]);
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const [grantForm, setGrantForm] = useState({
    officerId: '',
    expiryPreset: DEFAULT_EXPIRY_PRESET,
    note: '',
  });
  const [savingGrant, setSavingGrant] = useState(false);

  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message }

  const groupId = group?.id;
  const meetingId = group?.activeMeetingId || null;

  const loadOfficers = useCallback(async () => {
    setLoadingOfficers(true);
    try {
      const response = await diaryAPI.utils.getOfficers();
      const officers = response.results || response || [];
      setAvailableOfficers(officers);

      // Pre-check officers already on the meeting. GroupsView only gives us
      // names/avatars for the current officers, not ids, so match on the
      // formatted name - same approach DetailedInfo.js's reschedule form
      // uses for the same reason.
      const currentNames = (group?.officers || []).map((o) => o.name);
      const preselected = officers
        .filter((o) => currentNames.includes(officerDisplayName(o)))
        .map((o) => o.id);
      setSelectedOfficerIds(preselected);
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to load officers: ' + handleAPIError(err) });
    } finally {
      setLoadingOfficers(false);
    }
  }, [group]);

  const loadGrants = useCallback(async () => {
    if (!groupId) return;
    setLoadingGrants(true);
    try {
      const response = await diaryAPI.accessGrants.getGrants({ group_id: groupId });
      setGrants(response || []);
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to load access grants: ' + handleAPIError(err) });
    } finally {
      setLoadingGrants(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (open && group) {
      setTab(0);
      setFeedback(null);
      setGrantForm({
        officerId: '',
        expiryPreset: DEFAULT_EXPIRY_PRESET,
        note: '',
      });
      loadOfficers();
      loadGrants();
    }
  }, [open, group]);

  const handleOfficerToggle = (officerId) => {
    setSelectedOfficerIds((prev) =>
      prev.includes(officerId) ? prev.filter((id) => id !== officerId) : [...prev, officerId]
    );
  };

  const handleSelectAllOfficers = () => {
    setSelectedOfficerIds(availableOfficers.map((o) => o.id));
  };

  const handleClearOfficers = () => {
    setSelectedOfficerIds([]);
  };

  const handleSaveMeetingOfficers = async () => {
    if (!meetingId) return;
    if (selectedOfficerIds.length === 0) {
      setFeedback({ type: 'error', message: 'Select at least one officer.' });
      return;
    }

    setSavingMeetingOfficers(true);
    setFeedback(null);
    try {
      await diaryAPI.meetings.assignOfficers(meetingId, selectedOfficerIds);
      // Same pattern as Reschedule/End Meeting: close on success and let
      // the parent show the confirmation toast, rather than leaving the
      // dialog sitting open on top of its own inline alert.
      if (onUpdated) onUpdated('Meeting officers updated.');
      onClose();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to assign officers: ' + handleAPIError(err) });
    } finally {
      setSavingMeetingOfficers(false);
    }
  };

  const computeExpiresAt = () => {
    const preset = EXPIRY_PRESETS.find((p) => p.key === grantForm.expiryPreset) || EXPIRY_PRESETS[0];
    const d = new Date();
    d.setHours(d.getHours() + preset.hours);
    return d;
  };

  const handleCreateGrant = async () => {
    if (!grantForm.officerId) {
      setFeedback({ type: 'error', message: 'Select an officer to grant access to.' });
      return;
    }
    const expiresAt = computeExpiresAt();
    if (!expiresAt || Number.isNaN(expiresAt.getTime())) {
      setFeedback({ type: 'error', message: 'Choose when this grant should expire.' });
      return;
    }

    setSavingGrant(true);
    setFeedback(null);
    try {
      await diaryAPI.accessGrants.createGrant({
        officer_id: grantForm.officerId,
        group_id: groupId,
        meeting_id: meetingId,
        expires_at: expiresAt.toISOString(),
        note: grantForm.note,
      });
      setFeedback({ type: 'success', message: 'Access granted.' });
      setGrantForm((prev) => ({ ...prev, officerId: '', note: '', expiryPreset: DEFAULT_EXPIRY_PRESET }));
      await loadGrants();
      if (onUpdated) onUpdated();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to grant access: ' + handleAPIError(err) });
    } finally {
      setSavingGrant(false);
    }
  };

  const handleRevoke = async (grantId) => {
    setRevokingId(grantId);
    setFeedback(null);
    try {
      await diaryAPI.accessGrants.revokeGrant(grantId);
      await loadGrants();
      setFeedback({ type: 'success', message: 'Access revoked.' });
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to revoke access: ' + handleAPIError(err) });
    } finally {
      setRevokingId(null);
    }
  };

  if (!group) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Assign Officer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {group.group}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(e, val) => setTab(val)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<GroupsIcon fontSize="small" />} iconPosition="start" label="Meeting Officers" />
        <Tab icon={<LockClockIcon fontSize="small" />} iconPosition="start" label="Extended Data Access" />
      </Tabs>

      <DialogContent dividers sx={{ minHeight: 380 }}>
        {feedback && (
          <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        {/* -------------------- Tab 0: Meeting Officers -------------------- */}
        {tab === 0 && (
          <Box>
            {/* Which group + which meeting this assignment applies to - so
               it's never ambiguous when several groups' dialogs get opened
               back to back. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <EventIcon color="primary" fontSize="small" />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {group.group}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Meeting date: {formatMeetingDate(group.nextMeeting)}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Officers assigned here are the ones responsible for this group's current meeting.
              This is the same assignment shown against the group in the Detailed Info view.
            </Typography>

            {!meetingId ? (
              <Alert severity="warning">
                This group has no active meeting to assign officers to. Schedule a meeting first
                (from Detailed Info), then come back here to assign officers.
              </Alert>
            ) : loadingOfficers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOfficerIds.length} of {availableOfficers.length} selected
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" onClick={handleSelectAllOfficers}>
                      Select all
                    </Button>
                    <Button size="small" onClick={handleClearOfficers}>
                      Clear
                    </Button>
                  </Box>
                </Box>

                <List
                  dense
                  sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                  }}
                >
                  {availableOfficers.map((officer) => {
                    const selected = selectedOfficerIds.includes(officer.id);
                    return (
                      <ListItemButton
                        key={officer.id}
                        onClick={() => handleOfficerToggle(officer.id)}
                        selected={selected}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                          '&.Mui-selected:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.16),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={selected}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                        </ListItemIcon>
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: 13,
                            mr: 1.5,
                            bgcolor: selected ? 'primary.main' : 'action.selected',
                            color: selected ? 'primary.contrastText' : 'text.secondary',
                          }}
                        >
                          {officerInitials(officer)}
                        </Avatar>
                        <ListItemText primary={officerDisplayName(officer)} />
                      </ListItemButton>
                    );
                  })}
                  {availableOfficers.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No officers available.
                    </Typography>
                  )}
                </List>
              </>
            )}
          </Box>
        )}

        {/* ---------------- Tab 1: Extended Data Access ---------------- */}
        {tab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              An officer normally only syncs a group's member data around the meeting date
              (the day before, the meeting day, and the day after). Use this to grant an officer
              access for a limited time when that's not enough - the meeting was too long ago,
              or data needs updating ahead of an upcoming meeting.
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="grant-officer-label">Officer</InputLabel>
              <Select
                labelId="grant-officer-label"
                label="Officer"
                value={grantForm.officerId}
                onChange={(e) => setGrantForm((prev) => ({ ...prev, officerId: e.target.value }))}
              >
                {availableOfficers.map((officer) => (
                  <MenuItem key={officer.id} value={officer.id}>
                    {officerDisplayName(officer)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Grant expires in
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {EXPIRY_PRESETS.map((preset) => {
                const isSelected = grantForm.expiryPreset === preset.key;
                const isDefault = preset.key === DEFAULT_EXPIRY_PRESET;
                return (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    clickable
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => setGrantForm((prev) => ({ ...prev, expiryPreset: preset.key }))}
                    sx={{
                      fontWeight: isSelected ? 600 : 400,
                      px: isDefault ? 0.5 : 0,
                      // Default option keeps a light blue tint even when not
                      // the active selection, so it's always clear which
                      // one applies unless the user has deliberately picked
                      // something else.
                      ...(isDefault && !isSelected
                        ? {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            borderColor: alpha(theme.palette.primary.main, 0.4),
                            color: 'primary.main',
                          }
                        : {}),
                    }}
                  />
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              24 hours is applied by default.
            </Typography>

            <TextField
              label="Note (optional)"
              size="small"
              fullWidth
              placeholder="e.g. Officer missed last week's sync window, catching up"
              value={grantForm.note}
              onChange={(e) => setGrantForm((prev) => ({ ...prev, note: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleCreateGrant}
              disabled={savingGrant}
              startIcon={savingGrant ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Grant Access
            </Button>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Grants for this group
            </Typography>

            {loadingGrants ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : grants.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No access grants yet for this group.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Officer</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grants.map((grant) => {
                    const st = grantStatus(grant);
                    return (
                      <TableRow key={grant.id}>
                        <TableCell>{officerDisplayName(grant.officer)}</TableCell>
                        <TableCell>
                          {new Date(grant.expires_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={st.label} color={st.color} />
                        </TableCell>
                        <TableCell align="right">
                          {st.label === 'Active' && (
                            <Tooltip title="Revoke this grant now">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={revokingId === grant.id}
                                  onClick={() => handleRevoke(grant.id)}
                                >
                                  {revokingId === grant.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <BlockIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {tab === 0 && (
          <Button
            variant="contained"
            onClick={handleSaveMeetingOfficers}
            disabled={!meetingId || savingMeetingOfficers}
            startIcon={savingMeetingOfficers ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save Meeting Officers
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignOfficerDialog;